import update from 'immutability-helper';
import _ from 'lodash';
import { createAreaFromSizeInCM } from 'src/components/pages/productConfiguration/state/model/modelArea';
import { MAX_RESIZABLE_AREA_IN_PX, getStartingUnit } from 'src/components/widgets/areaDesigner/helpers';
import { createDefaultEditableModelOptions } from 'src/components/widgets/areaDesigner/models';
import { editableModelOptionsToModelOptions } from 'src/components/widgets/areaDesigner/modelsAdapters';
import { toGlobalID, toLocalID, uuid } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import { ProductConfigurationActionType } from '.';
import { ECommerce } from '../../products/__generated__/productList_models.graphql';
import { canSideRotationIsAvailable } from '../../products/helpers';
import { merchantProduct_item$data } from '../state/queries/__generated__/merchantProduct_item.graphql';
import { productProvider_listItem$data } from '../state/queries/__generated__/productProvider_listItem.graphql';
import { ProductProvider } from '../steps/1-addProduct/__generated__/addProductQuery.graphql';
import { matchColorsAttributes } from '../steps/4-variants/helpers';
import {
    Model,
    addColor,
    addEmptyModelSide,
    addNewColor,
    addSide,
    changeColorName,
    createEmpty,
    createModelFromDto,
    editColorAttributes,
    editColorJsonSettings,
    editColorMapping,
    editModelColorMappingsSalesChannels,
    getAllModelSides,
    mergeModelSide,
    removeColor,
    removeModelOptionForColor,
    removeModelSide,
    removeSide,
    setMerchantProduct,
    setProductImageNoEcommerce,
    setProductProviderCode,
    swapColorsPositions,
    upsertModelOptions
} from './model/model';
import { createDefaultModelColor, isDefaultColor } from './model/modelColor';
import { ModelSide, defaultModelSideImageUrl } from './model/modelSide';
import pages from './pages';
import { Side, getAllSidesFromQueryModel } from './side';
import { initialSide } from './stateInitialData';

export type DisabledModelSide = { colorGuid: string; sideGuid: string };

export interface ModelSideImageUpload {
    modelSide: ModelSide;
    progress: number;
}

export interface ProductConfigurationState {
    product: Model;
    salesChannelID: string;
    ecommerceSalesChannel: ECommerce[];
    merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'> | null;
    providerProduct: Omit<RelayFragment<productProvider_listItem$data>, ' $fragmentType'> | null;
    sides: Side[];
    currentStep: (typeof pages)[number]['key'];
    sideImageUploads: ModelSideImageUpload[];
    productImageUploadProgress: number | null;
    mode: 'none' | 'classic' | 'pod' | 'configurator';
    provider: ProductProvider;
    supportedVariants: boolean;
    disabledModelSides: DisabledModelSide[];
    headerDescriptionCustomComponent: React.ReactNode;
    alreadyDoneRedirectToSceneEditor: boolean;
}

export type ProductConfigurationStateReducer = (state: ProductConfigurationState, action: any) => ProductConfigurationState;
export type ProductConfigurationStateDispatch = (action: any) => void;

export const productConfigurationReducer = (state: ProductConfigurationState, action: ProductConfigurationActionType) => {
    const newState = _.clone(state);
    console.log('Product configuration state change', action.type, action);

    switch (action.type) {
        case 'MODE':
            newState.mode = action.mode;
            break;
        case 'SET_HEADER_DESCRIPTION_CUSTOM_COMPONENT':
            newState.headerDescriptionCustomComponent = action.component;
            break;
        case 'PROVIDER':
            newState.provider = action.provider;
            break;
        case 'SET_SALES_CHANNELS':
            newState.salesChannelID = action.salesChannelID;
            newState.ecommerceSalesChannel = action.ecommerces;
            // Check if the current sales channel supports variants
            // Product with AMAZON as master channel does not support variants
            newState.supportedVariants = !(
                action.salesChannels &&
                action.salesChannels.length > 0 &&
                action.salesChannels.some((x) => toLocalID(x.id) === 1 && x.ecommerceSettings?.ecommerceName === 'AMAZON')
            );
            break;
        case 'NEW_PRODUCT':
            newState.merchantProduct = null;
            newState.sides = [initialSide]; // Set a default side
            newState.product = createEmpty(initialSide);
            break;
        case 'NEW_PRODUCT_NO_INTEGRATION':
            newState.merchantProduct = null;
            newState.sides = [initialSide]; // Set a default side
            newState.product = createEmpty(initialSide);
            newState.product.code = uuid();
            newState.product.imageUrl = '';
            newState.product.name = '';
            newState.productImageUploadProgress = null;
            break;
        case 'EDIT_PRODUCT_NAME':
            newState.product.name = action.productName;
            break;
        case 'SET_PRODUCT_IMAGE':
            newState.product = setProductImageNoEcommerce(newState.product, action.imageUrl);
            break;
        case 'SET_PRODUCT_PROVIDER_CODE':
            newState.product = setProductProviderCode(newState.product, action.providerCode);
            break;
        case 'SELECT_MERCHANT_PRODUCT': {
            newState.merchantProduct = action.merchantProduct;
            newState.sides = [initialSide]; // Set a default side
            newState.product = createEmpty(initialSide);
            newState.product = setMerchantProduct(newState.product, action.merchantProduct, action.customThumbnail);
            break;
        }
        case 'SELECT_PROVIDER_PRODUCT': {
            newState.providerProduct = action.providerProduct;
            break;
        }
        case 'ADD_NEW_COLOR':
            if (!newState.product.colors.find((x) => matchColorsAttributes(x.code, action.values))) {
                newState.product = addNewColor(newState.product, action.values, action.mainSideImageUrl);

                // If first color is sample color, remove it.
                // This must be done after add new color to move sides to thew
                // new color.
                if (isDefaultColor(newState.product.colors[0])) {
                    newState.product = removeColor(newState.product, newState.product.colors[0]);
                }

                // Fix the position of the first color
                if (newState.product.colors.length > 0) {
                    newState.product = update(newState.product, {
                        colors: {
                            [0]: {
                                position: {
                                    $set: 0
                                }
                            }
                        }
                    });
                }
            }

            break;
        case 'EDIT_COLOR_NAME':
            newState.product = changeColorName(newState.product, action.color, action.name); // Remove color

            break;
        case 'EDIT_COLOR_NAME_AND_ATTRIBUTES':
            newState.product = editColorAttributes(newState.product, action.color, action.name, action.colorAttributes);

            break;

        case 'EDIT_COLOR_JSON_SETTINGS':
            newState.product = editColorJsonSettings(newState.product, action.color, action.settings);
            break;

        case 'EDIT_COLOR_MAPPING':
            newState.product = editColorMapping(newState.product, action.colorMapping);
            break;

        case 'EDIT_MODEL_COLOR_MAPPINGS_SALESCHANNELS':
            newState.product = editModelColorMappingsSalesChannels(newState.product, action.modelColorMappings);
            break;

        case 'REMOVE_COLOR': {
            // If no color remains, re-add sample color
            if (newState.product.colors.length == 1) {
                const defaultColor = createDefaultModelColor();
                defaultColor.sides = [];

                for (const modelSide of newState.product.colors[0].sides) {
                    const cloneSide = _.clone(modelSide);

                    // Generate new layers guids
                    cloneSide.areas = cloneSide.areas.map((x) => ({
                        ...x,
                        layers: x.layers.map((y) => ({
                            ...y,
                            id: toGlobalID(uuid(), 'AreaLayer')
                        }))
                    }));

                    defaultColor.sides.push(cloneSide);
                }

                newState.product = addColor(newState.product, defaultColor); // Add defautl color
            }

            newState.product = removeColor(newState.product, action.color); // Remove color
            break;
        }
        case 'REMOVE_ALL_COLORS': {
            if (newState.product.colors.length > 0) {
                const defaultColor = createDefaultModelColor();
                defaultColor.sides = [];

                for (const modelSide of newState.product.colors[0].sides) {
                    const cloneSide = _.clone(modelSide);

                    // Generate new layers guids
                    cloneSide.areas = cloneSide.areas.map((x) => ({
                        ...x,
                        layers: x.layers.map((y) => ({
                            ...y,
                            id: uuid()
                        }))
                    }));

                    defaultColor.sides.push(cloneSide);
                }
                newState.product.colors = [];
                newState.product = addColor(newState.product, defaultColor); // Add default color
            }
            break;
        }
        case 'SWAP_COLORS': {
            const modelColor1 = newState.product.colors.find((x) => x.guid == action.color1guid);
            const modelColor2 = newState.product.colors.find((x) => x.guid == action.color2guid);

            if (modelColor1 && modelColor2) newState.product = swapColorsPositions(newState.product, modelColor1, modelColor2);
            else throw new Error('One or both colors to swap not found');
            break;
        }
        case 'ADD_NEW_SIDE': {
            const newSide: Side = {
                sideID: null,
                name: action.sideName,
                guid: uuid()
            };

            newState.sides.push(newSide);
            newState.product = addSide(newState.product, newSide);
            break;
        }
        case 'EDIT_SIDE': {
            const side = newState.sides.find((x) => x.guid == action.modelSide.originalSideGuid);
            if (side) side.name = action.name;
            break;
        }
        case 'MERGE_MODEL_SIDE': {
            newState.product = mergeModelSide(newState.product, action.modelSide, action.newModelSide);
            console.log(newState.product);
            break;
        }
        case 'ADD_EMPTY_MODEL_SIDE': {
            newState.product = addEmptyModelSide(newState.product, action.modelColor, action.side);
            break;
        }
        case 'REMOVE_MODEL_SIDE': {
            newState.product = removeModelSide(newState.product, action.modelSide);
            break;
        }
        case 'SWAP_SIDES': {
            // Get the indexes from allModelSides because it will contain for sure one model side per original side.
            // We can't take these index for each color because one or more model side can be missing.
            const allModelSides = getAllModelSides(newState.product);
            const modelSide1 = allModelSides.find((x) => x.originalSideGuid == action.side1guid);
            const modelSide2 = allModelSides.find((x) => x.originalSideGuid == action.side2guid);

            if (modelSide1 && modelSide2) {
                const sortOrder1 = allModelSides.indexOf(modelSide1);
                const sortOrder2 = allModelSides.indexOf(modelSide2);

                for (const color of newState.product.colors) {
                    const _modelSide1 = color.sides.find((x) => x.originalSideGuid == modelSide1.originalSideGuid);
                    const _modelSide2 = color.sides.find((x) => x.originalSideGuid == modelSide2.originalSideGuid);
                    const colorIndex = newState.product.colors.indexOf(color);
                    const _modelSideIndex1 = _modelSide1 ? color.sides.indexOf(_modelSide1) : null;
                    const _modelSideIndex2 = _modelSide2 ? color.sides.indexOf(_modelSide2) : null;

                    if (_modelSideIndex1 != null) {
                        newState.product = update(newState.product, {
                            colors: {
                                [colorIndex]: {
                                    sides: {
                                        [_modelSideIndex1]: {
                                            $merge: { sortOrder: sortOrder2 }
                                        }
                                    }
                                }
                            }
                        });
                    }

                    if (_modelSideIndex2 != null) {
                        newState.product = update(newState.product, {
                            colors: {
                                [colorIndex]: {
                                    sides: {
                                        [_modelSideIndex2]: {
                                            $merge: { sortOrder: sortOrder1 }
                                        }
                                    }
                                }
                            }
                        });
                    }
                }
            }
            break;
        }
        case 'REMOVE_SIDE': {
            newState.product = removeSide(newState.product, action.side);
            newState.sides = update(newState.sides, { $splice: [[newState.sides.indexOf(action.side), 1]] });
            break;
        }
        case 'LOAD_PRODUCT': {
            newState.sides = getAllSidesFromQueryModel(action.model);
            newState.product = createModelFromDto(action.model, newState.sides);
            newState.provider = newState.product.provider;
            break;
        }
        case 'UPLOAD_SIDE_IMAGE_START': {
            newState.sideImageUploads.push({
                modelSide: action.modelSide,
                progress: 0
            });
            break;
        }
        case 'UPLOAD_SIDE_IMAGE_END': {
            newState.sideImageUploads = newState.sideImageUploads.filter((x) => x.modelSide.guid != action.modelSide.guid);
            break;
        }
        case 'UPLOAD_PRODUCT_IMAGE_PROGRESS': {
            newState.productImageUploadProgress = action.progress;
            break;
        }
        case 'UPLOAD_SIDE_IMAGE_PROGRESS': {
            newState.sideImageUploads.map((x) => {
                return x.modelSide.guid == action.modelSide.guid ? { ...x, progress: action.progress } : x;
            });
            break;
        }
        case 'EDIT_SIDE_MANDATORY_ALL_VARIANTS': {
            newState.product.colors.forEach((modelColor) => {
                modelColor.sides
                    .filter((x) => x.originalSideGuid == action.modelSide.originalSideGuid)
                    .forEach((side) => {
                        side.mandatory = action.mandatory;
                    });
            });
            break;
        }
        case 'EDIT_SIDE_MANDATORY_ALL_SIDES': {
            newState.product.colors.forEach((modelColor) => {
                modelColor.sides.forEach((side) => {
                    side.mandatory = action.mandatory;
                });
            });
            break;
        }
        case 'EDIT_SIDE_ROTATE_ALL_VARIANTS': {
            newState.product.colors.forEach((modelColor) => {
                modelColor.sides
                    .filter((x) => x.originalSideGuid == action.modelSide.originalSideGuid)
                    .forEach((side) => {
                        canSideRotationIsAvailable(newState.product, side, modelColor)[0] ? (side.canRotate = action.canRotate) : (side.canRotate = false);
                    });
            });
            break;
        }
        case 'EDIT_SIDE_ROTATE_ALL_SIDES': {
            newState.product.colors.forEach((modelColor) => {
                modelColor.sides.forEach((side) => {
                    canSideRotationIsAvailable(newState.product, side, modelColor)[0] ? (side.canRotate = action.canRotate) : (side.canRotate = false);
                });
            });
            break;
        }
        case 'UPSERT_MODEL_OPTIONS': {
            newState.product = upsertModelOptions(newState.product, action.modelOptions);
            break;
        }
        case 'REMOVE_MODEL_OPTIONS': {
            newState.product = removeModelOptionForColor(newState.product, action.modelColor);
            break;
        }
        case 'SET_DISABLED_SIDES': {
            newState.disabledModelSides = action.disabledModelSides;
            break;
        }
        case 'SET_PRODUCT_RESIZABLE': {
            // remove all colors and sides besides the first ones
            const newColors = newState.product.colors.filter((x) => x.colorID === 1);
            const newColor = newColors[0];
            const deletedSides = newColor.sides.splice(1, newState.product.colors[0].sides.length - 1);
            const originalSideGuids = deletedSides.map((x) => x.originalSideGuid);
            newState.sides = newState.sides.filter((x) => !originalSideGuids.includes(x.guid));

            const side = newColor.sides[0];
            const option = newState.product.options?.find((x) => x.colorGuid == newColor?.guid && x.sideGuid == null) ?? null;

            newState.product.colors = newColors;

            let newSide = side;
            const startWidthCM = 10; // 10cm
            const startHeightCM = 5; // 5cm
            const ppcm = MAX_RESIZABLE_AREA_IN_PX / Math.max(startWidthCM, startHeightCM);

            // update resizable options
            if (action.resizable) {
                // Set default name to product
                if (newState.product.name === '') {
                    newState.product.name = 'Resizable Product';
                }

                newColor.sides[0].imageUrl = defaultModelSideImageUrl;
                if (option) {
                    const newOption = { ...option, isResizable: action.resizable };
                    newState.product = upsertModelOptions(newState.product, newOption);
                } else {
                    if (newColor && newColor.sides[0]) {
                        let defaultModelOptions = createDefaultEditableModelOptions(newColor, null);
                        defaultModelOptions.mirrorType = 'COLORED';
                        defaultModelOptions.edgesColor = '#FFFFFF';

                        const modelOptions = editableModelOptionsToModelOptions(defaultModelOptions, ppcm, getStartingUnit());

                        let newOption = { ...modelOptions, isResizable: action.resizable };
                        newState.product = upsertModelOptions(newState.product, newOption);
                    }
                }
            } else {
                newColor.sides[0].imageUrl = newState.product.imageUrl ?? defaultModelSideImageUrl;
                newState.product.options = [];
            }

            // update side
            if (action.resizable) {
                newSide = update(side, {
                    ppcm: { $set: ppcm },
                    ['areas']: {
                        $set: [
                            {
                                ...createAreaFromSizeInCM(startWidthCM, startHeightCM, ppcm)
                            }
                        ]
                    }
                });
            } else {
                newSide = update(side, {
                    ppcm: { $set: 0 },
                    ['areas']: {
                        $set: []
                    }
                });
            }

            newState.product = mergeModelSide(newState.product, side, newSide);
            break;
        }
        case 'TOGGLE_AR_FOR_RESIZABLE': {
            if (action.arEnabled) {
                newState.product.sceneConfig = {
                    ...newState.product.sceneConfig,
                    general: {
                        ...newState.product.sceneConfig?.general,
                        arEnabled: true,
                        arAnchor: 'wall'
                    }
                };
            } else {
                newState.product.sceneConfig = {
                    ...newState.product.sceneConfig,
                    general: {
                        ...newState.product.sceneConfig?.general,
                        arEnabled: false,
                        arAnchor: null
                    }
                };
            }
            break;
        }
        case 'SET_ALREADY_DONE_REDIRECT_TO_SCENE_EDITOR': {
            newState.alreadyDoneRedirectToSceneEditor = action.value;
            break;
        }
        default:
            throw new Error('Invalid product configuration dispatcher action.');
    }

    console.log(newState);
    return newState;
};
